import jwtDecode from "jwt-decode";
import dayjs from "dayjs";
import _uniq from "lodash/uniq";

import api, {
  apiAuthorize,
  apiDeauthorize,
  getApiErrorMessage,
} from "../../helpers/api";
import { actions as additionalInfoActions } from "../additional-info";
import { actions as alertsActions } from "../alerts";
import { actions as compareActions } from "../compare";
import { actions as emailOptInActions } from "../email-opt-in";
import { actions as listingActions } from "../listing";
import { actions as searchActions } from "../search";
import { actions as userActions } from "../user";

import { URL_API_LOGIN, URL_API_LOGOUT } from "../../constants";

/**
 * Action types
 * @property {string} AUTH_CLEARED
 * @property {string} AUTH_EXPIRED
 * @property {string} AUTH_MESSAGE_CLEAR
 * @property {string} AUTH_SUCCESS
 */
export const types = {
  AUTH_CLEARED: "AUTH_CLEARED",
  AUTH_EXPIRED: "AUTH_EXPIRED",
  AUTH_MESSAGE_CLEAR: "AUTH_MESSAGE_CLEAR",
  AUTH_REPLACE: "AUTH_REPLACE",
  AUTH_SUCCESS: "AUTH_SUCCESS",
};

/**
 * @typedef {object} state.auth
 * @property {string} action.access_token
 * @property {number} expires - UNIX timestamp retrieved from JWT
 * @property {string} message - Message displayed if the authentication token is rejected
 */
export const initialState = {
  access_token: null,
  expires: null,
  last_update: null,
  message: null,
};

/**
 * @namespace Reducers
 * These reducers are all nested under the login property of the Redux state
 * because they are persisted separately to local storage instead of
 * session storage
 */
export const reducers = {
  /**
   * Updates auth state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type - `AUTH_SUCCESS`, `AUTH_EXPIRED`, or no change
   * @param {string} action.access_token
   * @param {number} action.expires
   * @param {string} action.last_update
   * @param {string} action.message
   * @return {state.auth}
   * @memberof Reducers
   */
  auth: (state = initialState, action) => {
    switch (action.type) {
      case types.AUTH_CLEARED:
        return { ...initialState, last_update: dayjs().format() };
      case types.AUTH_EXPIRED:
        return {
          ...state,
          access_token: null,
          expires: null,
          last_update: dayjs().format(),
          message: action.message,
        };
      case types.AUTH_MESSAGE_CLEAR:
        return { ...state, message: null };
      case types.AUTH_REPLACE:
        return {
          ...state,
          access_token: action.access_token,
          expires: action.expires,
          last_update: action.last_update,
          message: action.message,
        };
      case types.AUTH_SUCCESS:
        return {
          access_token: action.access_token,
          expires: action.expires,
          last_update: dayjs().format(),
          message: null,
        };
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Checks if the authentication expires time can be
   * found in the Redux store or local storage, and
   * then either attempts to reload the page if
   * the Redux store and local storage don't match,
   * or adds the authentication header to API requests,
   * or expires the current authentication
   * @memberof Actions
   * @returns {bool}
   */
  check: () => (dispatch, getState) => {
    let state = getState();
    let auth =
      !!state.login && state.login.auth ? { ...state.login.auth } : null;

    try {
      if (!!auth && !!auth.last_update) {
        let localState = localStorage.getItem("persist:auth");
        localState = !!localState ? JSON.parse(localState) : null;
        if (!!localState) {
          let {
            auth: localAuth,
            user: localUser,
            userWatchedListings: localUserWatchedListings,
          } = localState;
          localAuth = !!localAuth ? JSON.parse(localAuth) : null;

          if (
            !!localAuth &&
            !!localAuth.last_update &&
            dayjs(localAuth.last_update).isAfter(dayjs(auth.last_update))
          ) {
            if (!!localAuth.access_token) {
              localUser = !!localUser ? JSON.parse(localUser) : null;
              localUserWatchedListings = !!localUserWatchedListings
                ? JSON.parse(localUserWatchedListings)
                : null;

              dispatch(actions.replace(localAuth));
              dispatch(userActions.update(localUser));
              dispatch(
                userActions.updateWatchedListings(localUserWatchedListings)
              );
              auth = localAuth;
            } else {
              dispatch(actions.logout());
            }
          }
        }
      }
    } catch (e) {}

    if (auth.expires) {
      if (dayjs.unix(auth.expires).isAfter(dayjs())) {
        apiAuthorize(auth.access_token);
        dispatch(emailOptInActions.handleOptIn(false));
        return true;
      } else if (!auth.message) {
        dispatch(
          actions.expire(!!auth.access_token ? "Authentication expired" : null)
        );
      }
    }

    return false;
  },

  /**
   * Clear authenticated data from store
   *
   * @param {string} message
   * @memberof Actions
   */
  clearAuth: message => dispatch => {
    apiDeauthorize();
    dispatch({ type: types.AUTH_CLEARED });
    dispatch(additionalInfoActions.clear());
    dispatch(alertsActions.clear());
    dispatch(compareActions.clear());
    dispatch(listingActions.clear());
    dispatch(searchActions.clear());

    if (message) {
      dispatch(actions.expiredMessage(message));
    } else {
      dispatch(emailOptInActions.handleOptIn(true));
      dispatch(userActions.clear());
      dispatch(userActions.clearWatchedListings());
    }
  },

  /**
   * Clear auth message
   * @memberof Actions
   */
  clearMessage: () => ({
    type: types.AUTH_MESSAGE_CLEAR,
  }),

  /**
   * Dispatches the actions to set the authentication message
   * and destroy the current authentication
   * @param {string} message
   * @memberof Actions
   */
  expire: message => dispatch => {
    dispatch(actions.clearAuth(message));
  },

  /**
   * Sets the authentication message displayed on the login
   * page when the user's authentication expires
   * @param {string} message
   * @memberof Actions
   * @returns {object}
   */
  expiredMessage: message => ({
    type: types.AUTH_EXPIRED,
    message: message,
  }),

  /**
   * Posts the user's login credentials to the API and then either
   * dispatches the action to update the authentication state and
   * save the authentication to local storage or handle any errors
   * returned by the API
   * @memberof Actions
   * @returns {Promise}
   */
  login: data => dispatch => {
    return api
      .post(URL_API_LOGIN, data)
      .then(response => {
        if (response.data && response.data.access_token && response.data.user) {
          dispatch(actions.update(response.data));
          return { success: true };
        } else {
          const message = getApiErrorMessage(response, true);
          return { success: false, message: message };
        }
      })
      .catch(error => {
        const message = getApiErrorMessage(error, true);

        return {
          success: false,
          message: message,
        };
      });
  },

  /**
   * Requests that API invalidate the JWT currently being used
   * to authenticate the user and then dispatches actions to
   * destroy the current authentication and reset the Redux store
   * @memberof Actions
   */
  logout: () => (dispatch, getState) => {
    let state = getState();

    if (state.login.auth.access_token) {
      api
        .get(URL_API_LOGOUT)
        .catch(error => {
          getApiErrorMessage(error, true);
        })
        .then(() => {
          dispatch(actions.clearAuth());
        });
    } else {
      dispatch(actions.clearAuth());
    }
  },

  /**
   * Replace auth data
   * @param {object} replacement
   * @memberof Actions
   */
  replace: replacement => ({
    type: types.AUTH_REPLACE,
    ...replacement,
  }),

  /**
   * Sets the authentication state with the decoded authentication
   * @param {object} decoded
   * @memberof Actions
   * @returns {object}
   */
  success: decoded => ({
    type: types.AUTH_SUCCESS,
    access_token: decoded.access_token,
    expires: decoded.exp,
  }),

  /**
   * Decodes the JWT contained within the current authentication
   * and either dispatches the action to expire the authentication
   * if something is wrong with it or dispatches the action to set
   * new authentication state and add the authentication header to
   * API requests
   * @param {object} auth
   * @param {string} access_token - JWT
   * @param {object} user
   * @memberof Actions
   */
  update: auth => dispatch => {
    try {
      const decoded = jwtDecode(auth.access_token);

      if (!decoded || !decoded.exp) {
        dispatch(actions.expire("Authentication not found"));
      }

      decoded.access_token = auth.access_token;

      dispatch(actions.success(decoded));
      dispatch(userActions.update(auth.user));
      dispatch(emailOptInActions.handleOptIn(false));
      apiAuthorize(auth.access_token);

      if (
        !!auth.watchedListings &&
        Array.isArray(auth.watchedListings) &&
        !!auth.watchedListings.length
      ) {
        dispatch(
          userActions.updateWatchedListings(
            _uniq(auth.watchedListings)
              .map(id => parseInt(id, 10))
              .sort((a, b) => a - b)
          )
        );
      }
    } catch (error) {
      const errorMessage =
        error.name && error.name === "InvalidTokenError"
          ? "Authentication invalid"
          : null;
      dispatch(actions.expire(errorMessage));
    }
  },
};
